import type { FeatureCollection, Point } from 'geojson'
import type { DataDrivenPropertyValueSpecification, GeoJSONSource, Map } from 'maplibre-gl'
import { mergeCrowded, type CountedMark, type Obstacle } from './crowding'
import { SLOT_DOT } from './slots'
import {
  CHIP_SHADOW_COLOR,
  MARK_COUNT_FONT,
  SELECTED_FILL_COLOR,
  SIZE_CLASSES,
  WINERY_CLUSTER_COLOR,
  WINERY_CLUSTER_KIND,
  WINERY_CLUSTER_LABEL_COLOR,
  WINERY_CLUSTER_COUNT_LAYER_ID,
  WINERY_CLUSTER_LAYER_ID,
  WINERY_CLUSTER_SHADOW_LAYER_ID,
  WINERY_DERIVED_POINT_OPACITY,
  WINERY_HALO_LAYER_ID,
  WINERY_HOVER_STROKE_COLOR,
  WINERY_KEEPOUT_LAYER_ID,
  WINERY_POINT_KIND,
  WINERY_POINT_OPACITY,
  WINERY_POINT_LAYER_ID,
  WINERY_SIZE_COLORS,
  WINERY_SOURCE_ID,
  WINERY_STROKE_COLOR,
  WINERY_UNKNOWN,
  WINERY_UNKNOWN_COLOR,
} from '../../lib/map_config'
import type { WineryLayer } from '../../types/atlas'

/**
 * The winery layer: one source, a cluster chip with its shadow and its count,
 * and a point circle coloured by size class with a halo for the selected one.
 *
 * It sits in two places in the stack (slots.ts), and neither is under a region.
 *
 * - THE CHIPS name no slot. They are appended with the style, so they sit above
 *   every slot and therefore above every appellation layer and every name,
 *   including a tier that arrives later. A count is a label laid ON the map.
 * - THE DOTS go under the dot slot: above every fill, ribbon and hairline —
 *   a dot inside two regions is drawn in its own colour, not read through
 *   theirs — above every basemap label, and UNDER the appellations' names.
 *   They used to be appended too, which put a dot on top of whatever name it
 *   shared ground with; and seventy producers share their ground with the name
 *   of the valley they are in, which is the name a reader came for.
 *
 * With the dots goes a layer that draws nothing: the keep-out, the room each
 * dot keeps clear of town names. It is a symbol layer, because symbol placement
 * is the only thing a town's name gives way to, and its place in the stack is
 * what decides who gives way: above the basemap's names, so they are placed
 * after it and step aside, and below the appellations' names, which are placed
 * before it and do not. An appellation's name may cross its own producers; a
 * town's must not be printed under them.
 *
 * Clustering happens on the SERVER (KTD10), not in MapLibre's own `cluster: true`
 * — that clusters client-side over whatever GeoJSON it was handed, which is the
 * 2.8 MB payload the whole decision exists to avoid sending. What arrives here is
 * already either cells or dots for the current viewport under the current
 * filters, and `mode` says which.
 *
 * Both modes share one source, because a mode change is then a `setData` rather
 * than adding and removing a second source mid-pan. Each feature carries the
 * kind it is and the two layer groups filter on it.
 *
 * Types are imported from maplibre-gl, values never: this module is reached only
 * through use_maplibre.ts, which is itself behind the layout's dynamic import.
 */

export type WineryFeatureProperties = {
  kind: string
  count: number
  slug: string
  name: string
  // Never null: a MapLibre `match` expression cannot match on null, so KTD11's
  // unknown is spelled as the string it is spelled as everywhere else.
  size_class: string
  // KTD6. The dot is the surface where a 0.25 guess is most likely to be read as
  // a fact, because it is the one place with no source line beside it — so the
  // distinction has to be DATA the paint expression can read, not a style choice
  // made over `size_class` alone. False for a cluster, which stands for a mix of
  // producers and makes no claim about any of them.
  size_stated: boolean
}

export type WineryFeatureCollection = FeatureCollection<Point, WineryFeatureProperties>

// A chip is sized by the count it stands for, because a cell holding 3,000
// producers and one holding 4 are the same chip otherwise, and the number is
// the only thing a cluster has to say. By the SQUARE ROOT of the count, so it
// is the chip's area that grows with it: 9 px for one producer, 27 px for five
// thousand. As `[sqrt(count), px]` stops, ONE table for the paint expression
// and for the arithmetic below, which has to agree with it to the pixel.
const CLUSTER_RADIUS_STOPS: readonly [number, number][] = [
  [1, 9],
  [10, 14],
  [32, 20],
  [72, 27],
]

/** How big the chip for a count is drawn, in px: what the paint works out. */
export function clusterRadiusFor(count: number): number {
  const stops = CLUSTER_RADIUS_STOPS
  const root = Math.sqrt(Math.max(count, 0))
  const last = stops[stops.length - 1]
  if (root <= stops[0][0]) return stops[0][1]
  if (root >= last[0]) return last[1]

  const next = stops.findIndex(([at]) => at > root)
  const [fromAt, fromPx] = stops[next - 1]
  const [toAt, toPx] = stops[next]

  return fromPx + ((toPx - fromPx) * (root - fromAt)) / (toAt - fromAt)
}

// The clear ground two chips keep between their FACES. A chip is drawn with a
// 1.5 px ring outside its radius and a soft shadow outside that, so two whose
// radii merely do not overlap still read as touching.
const CHIP_GAP_PX = 5

// Five decimals, as the server rounds what it sends (`ECHO_PRECISION`).
const roundDegrees = (value: number) => Math.round(value * 1e5) / 1e5

/**
 * The chips a response is drawn as: the server's cells, with any two whose
 * chips would overlap at the zoom the response answered made into one
 * (crowding.ts has why — the world view used to open on "156" printed over
 * "311"). The counts still add up to what the server sent, which is what the
 * truncation notice reads.
 *
 * The array it was handed when nothing overlaps, which is the usual case and
 * keeps this free.
 */
export function wineryChips(layer: WineryLayer): readonly CountedMark[] {
  let chips = chipsOf.get(layer)

  if (!chips) {
    chips = mergeCrowded(layer.clusters, layer.zoom, clusterRadiusFor, CHIP_GAP_PX)
    chipsOf.set(layer, chips)
  }

  return chips
}

// Worked out once per response: the source asks, and so does whatever has to
// keep clear of the chips.
const chipsOf = new WeakMap<WineryLayer, readonly CountedMark[]>()

const NO_OBSTACLES: readonly Obstacle[] = []

/**
 * The chips as things to keep off: where each is and how big it is drawn. What
 * the reader's own counted marks step clear of (journal_layers.ts). Nothing
 * when the layer is showing dots — a tasting among dots is a mark among marks,
 * and there is no number under it to hide.
 */
export function wineryObstacles(layer: WineryLayer | null): readonly Obstacle[] {
  if (!layer || layer.mode !== 'clusters') return NO_OBSTACLES

  return wineryChips(layer).map(({ lng, lat, count }) => ({ lng, lat, radius: clusterRadiusFor(count) }))
}

// One FeatureCollection whichever mode the server answered in. `count` is 1 for a
// point so the label expression and any future sizing rule read one property
// rather than branching.
export function wineryFeatures(layer: WineryLayer | null): WineryFeatureCollection {
  if (layer === null) return { type: 'FeatureCollection', features: [] }

  const clusters = wineryChips(layer).map((cluster) => ({
    type: 'Feature' as const,
    properties: {
      kind: WINERY_CLUSTER_KIND,
      count: cluster.count,
      slug: '',
      name: '',
      size_class: WINERY_UNKNOWN,
      size_stated: false,
    },
    geometry: { type: 'Point' as const, coordinates: [roundDegrees(cluster.lng), roundDegrees(cluster.lat)] },
  }))

  const points = layer.wineries.map((winery) => ({
    type: 'Feature' as const,
    properties: {
      kind: WINERY_POINT_KIND,
      count: 1,
      slug: winery.slug,
      name: winery.name,
      size_class: winery.size_class ?? WINERY_UNKNOWN,
      size_stated: winery.size_stated,
    },
    geometry: { type: 'Point' as const, coordinates: [winery.lng, winery.lat] },
  }))

  return { type: 'FeatureCollection', features: [...clusters, ...points] }
}

// Every layer, bottom of the stack first, which is what a visibility toggle has
// to reach. The keep-out goes with the dots: a hidden layer is not placed, so a
// reader who turns the wineries off gets the towns' names back on their points.
export function wineryLayerIds(): string[] {
  return [
    WINERY_KEEPOUT_LAYER_ID,
    WINERY_HALO_LAYER_ID,
    WINERY_POINT_LAYER_ID,
    WINERY_CLUSTER_SHADOW_LAYER_ID,
    WINERY_CLUSTER_LAYER_ID,
    WINERY_CLUSTER_COUNT_LAYER_ID,
  ]
}

// A click and the pointer are scoped to the two circle layers. The count label
// is excluded for the same reason the region labels are: a symbol layer's hit
// area is the glyph, not the mark. The shadow and the halo are excluded because
// they are decoration around a mark, not the mark: a click in a chip's shadow
// is a click on the map beside it.
export function wineryInteractiveLayerIds(): string[] {
  return [WINERY_CLUSTER_LAYER_ID, WINERY_POINT_LAYER_ID]
}

// KTD11's neutral is the DEFAULT of the match, not a fifth case: a class this
// build has never heard of — one a later phase adds — reads as unknown rather
// than as undrawn.
export function wineryColorFor(sizeClass: string | null): string {
  if (sizeClass === null) return WINERY_UNKNOWN_COLOR

  return WINERY_SIZE_COLORS[sizeClass] ?? WINERY_UNKNOWN_COLOR
}

/**
 * KTD6's opacity axis: a dot whose size class is an inference draws weaker than
 * one backed by a stated figure.
 *
 * NOT an outline. The point layer already sets a pale `circle-stroke-color`,
 * and spends that ring on hover and selection, so an outline treatment would
 * collide with it rather than read as a new signal — and P6a's KTD11
 * established a solid neutral colour for `unknown`, not an outline convention,
 * so there is no outline-versus-fill vocabulary here to reuse. The legend
 * carries a line for this axis because a dot has no room for the word the badge
 * carries, and a faded dot with no key reads as a rendering fault.
 *
 * **`unknown` draws at full strength.** It is the absence of a class rather than
 * a weak one, so there is nothing for the fade to qualify; fading it would say
 * "this claim is shaky" about a dot that makes no claim, and after P6c that is
 * still most of the map. The fade then means exactly one thing: the COLOUR you
 * are reading is an estimate.
 *
 * Independent of selection and of hover, and so is the colour. A selected dot
 * used to be repainted amber, which erased the one fact a dot carries; it now
 * keeps its size colour at this opacity and gains an amber ring and halo. A
 * selected derived dot that brightened to full would be the one place a guess
 * still read as a fact.
 */
export function winerySizeOpacity(): DataDrivenPropertyValueSpecification<number> {
  return [
    'case',
    ['==', ['get', 'size_class'], WINERY_UNKNOWN],
    WINERY_POINT_OPACITY,
    ['==', ['get', 'size_stated'], true],
    WINERY_POINT_OPACITY,
    WINERY_DERIVED_POINT_OPACITY,
  ] as unknown as DataDrivenPropertyValueSpecification<number>
}

// The size class and nothing else. It used to open with a `case` on the selected
// slug; selection is a feature-state now, and it changes the ring, not the fill.
export function winerySizeColor(): DataDrivenPropertyValueSpecification<string> {
  return [
    'match',
    ['get', 'size_class'],
    ...SIZE_CLASSES.flatMap((sizeClass) => [sizeClass, WINERY_SIZE_COLORS[sizeClass]]),
    WINERY_UNKNOWN_COLOR,
  ] as unknown as DataDrivenPropertyValueSpecification<string>
}

const zoomRamp = (...stops: unknown[]) => ['interpolate', ['linear'], ['zoom'], ...stops]
const selected = ['boolean', ['feature-state', 'selected'], false]
const hovered = ['boolean', ['feature-state', 'hover'], false]

// `CLUSTER_RADIUS_STOPS`, as the paint expression.
const clusterRadius = ['interpolate', ['linear'], ['sqrt', ['get', 'count']], ...CLUSTER_RADIUS_STOPS.flat()]

export function addWineryLayers(map: Map, layer: WineryLayer | null, selectedSlug: string | null) {
  // A style swap discards the previous style's sources, so this runs again after
  // a basemap failure — but addSource throws on a duplicate id.
  if (map.getSource(WINERY_SOURCE_ID)) {
    setWineryData(map, layer)
    return
  }

  // `promoteId` makes the slug the feature id, which is what hover and
  // selection address a dot by. Every cluster shares the empty slug and is
  // therefore not addressable — a cluster is neither hovered nor selected.
  map.addSource(WINERY_SOURCE_ID, { type: 'geojson', data: wineryFeatures(layer), promoteId: 'slug' })

  const clustersOnly = ['==', ['get', 'kind'], WINERY_CLUSTER_KIND] as never
  const pointsOnly = ['==', ['get', 'kind'], WINERY_POINT_KIND] as never

  // --- the dots, under the dot slot ------------------------------------------

  // The room a dot keeps. One invisible letter per producer, about the size of
  // its dot, always placed (`text-allow-overlap`) and therefore always in the
  // way of whatever is placed after it. In the roman the map has already
  // loaded, so it asks for no glyph. The cost is one collision box per dot on
  // screen in every placement pass — a few hundred at most, since past that
  // the server sends clusters.
  map.addLayer(
    {
      id: WINERY_KEEPOUT_LAYER_ID,
      type: 'symbol',
      source: WINERY_SOURCE_ID,
      filter: pointsOnly,
      layout: {
        'text-field': 'o',
        'text-font': MARK_COUNT_FONT,
        'text-size': zoomRamp(8, 9, 14, 17) as never,
        'text-allow-overlap': true,
        'text-padding': 0,
      },
      paint: { 'text-opacity': 0 },
    },
    SLOT_DOT,
  )

  // The amber glow behind the ONE dot an open panel describes. Its own layer,
  // under the dots, because `circle-blur` softens the whole circle and the dot
  // itself has to stay crisp. Invisible for every other feature.
  map.addLayer(
    {
      id: WINERY_HALO_LAYER_ID,
      type: 'circle',
      source: WINERY_SOURCE_ID,
      filter: pointsOnly,
      paint: {
        'circle-color': SELECTED_FILL_COLOR,
        'circle-radius': zoomRamp(8, 12, 14, 22) as never,
        'circle-blur': 0.35,
        'circle-opacity': ['case', selected, 0.5, 0] as never,
      },
    },
    SLOT_DOT,
  )

  // No `layout` at all: the dots are a plain circle layer, and everything that
  // moves — hover, selection — is feature-state read by the paint below, which
  // costs a repaint and never a relayout of the source.
  map.addLayer(
    {
      id: WINERY_POINT_LAYER_ID,
      type: 'circle',
      source: WINERY_SOURCE_ID,
      filter: pointsOnly,
      paint: {
        'circle-color': winerySizeColor(),
        'circle-opacity': winerySizeOpacity(),
        // Small where the camera first shows dots, so seventy producers in one
        // valley are seventy marks rather than a smear, and growing as it closes in.
        'circle-radius': zoomRamp(
          8,
          ['case', selected, 5.5, hovered, 4.5, 3],
          14,
          ['case', selected, 10, hovered, 8.5, 6.5],
        ) as never,
        // The ring says WHICH dot, never what size: amber for the one the panel
        // describes, ink under the pointer, paper otherwise. It is why the derived
        // treatment is opacity rather than an outline (KTD6).
        'circle-stroke-color': [
          'case',
          selected,
          SELECTED_FILL_COLOR,
          hovered,
          WINERY_HOVER_STROKE_COLOR,
          WINERY_STROKE_COLOR,
        ] as never,
        'circle-stroke-width': zoomRamp(8, ['case', selected, 2.5, 1], 14, ['case', selected, 3, 1.5]) as never,
      },
    },
    SLOT_DOT,
  )

  // --- the chips, appended: above every slot, and every name -----------------

  // A soft ink shadow a pixel and a half below the chip, which is what makes it
  // a label laid on the map rather than a mark printed in it.
  map.addLayer({
    id: WINERY_CLUSTER_SHADOW_LAYER_ID,
    type: 'circle',
    source: WINERY_SOURCE_ID,
    filter: clustersOnly,
    paint: {
      'circle-color': CHIP_SHADOW_COLOR,
      'circle-opacity': 0.16,
      'circle-blur': 0.6,
      'circle-translate': [0, 1.5],
      'circle-radius': ['+', clusterRadius, 4] as never,
    },
  })

  map.addLayer({
    id: WINERY_CLUSTER_LAYER_ID,
    type: 'circle',
    source: WINERY_SOURCE_ID,
    filter: clustersOnly,
    paint: {
      'circle-color': WINERY_STROKE_COLOR,
      'circle-opacity': 0.96,
      'circle-radius': clusterRadius as never,
      'circle-stroke-color': WINERY_CLUSTER_COLOR,
      'circle-stroke-width': 1.5,
    },
  })

  map.addLayer({
    id: WINERY_CLUSTER_COUNT_LAYER_ID,
    type: 'symbol',
    source: WINERY_SOURCE_ID,
    filter: clustersOnly,
    layout: {
      'text-field': ['to-string', ['get', 'count']] as never,
      'text-font': MARK_COUNT_FONT,
      'text-size': ['interpolate', ['linear'], ['sqrt', ['get', 'count']], 1, 10.5, 72, 13] as never,
      'text-allow-overlap': true,
      // The count is always drawn, and it keeps about a small chip's worth of
      // room round itself: an appellation's name is placed after it and steps
      // off the chip rather than running under it.
      'text-padding': 9,
    },
    // No halo. There was one, in the text's own colour, to thicken Noto Sans
    // Regular towards a semibold; the roman cut is drawn a shade heavier than
    // regular already (`MARK_COUNT_FONT`), and thickened again its numerals
    // closed up at chip size.
    paint: { 'text-color': WINERY_CLUSTER_LABEL_COLOR },
  })

  setWinerySelection(map, selectedSlug)
}

export function setWineryData(map: Map, layer: WineryLayer | null) {
  map.getSource<GeoJSONSource>(WINERY_SOURCE_ID)?.setData(wineryFeatures(layer))
}

export function setWineryVisibility(map: Map, visible: boolean) {
  for (const id of wineryLayerIds()) {
    if (!map.getLayer(id)) continue
    map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none')
  }
}

// Which dot each map currently marks as selected, so the mark can be taken off
// it again: a feature-state is addressed by id, and there is no "whichever one
// has it". Weak, so a removed map takes its entry with it.
const selectedDots = new WeakMap<Map, string>()

/**
 * Marks the producer an open panel describes.
 *
 * A feature-state, not a repaint. This used to replace the point layer's
 * `circle-color` with a new `case` naming the slug, and MapLibre answers a
 * data-driven `setPaintProperty` with a relayout of the whole source — on every
 * open and every close. A feature-state costs one repaint, and the source keeps
 * it across `setData`: the ring survives every pan's fresh viewport without
 * being re-applied, and a slug that is not on screen yet is marked the moment
 * its dot arrives.
 */
export function setWinerySelection(map: Map, selectedSlug: string | null) {
  // Addressing a source that does not exist is an `error` event, and the map
  // reads those as the basemap failing.
  if (!map.getSource(WINERY_SOURCE_ID)) return

  const previous = selectedDots.get(map)
  if (previous && previous !== selectedSlug) {
    map.removeFeatureState({ source: WINERY_SOURCE_ID, id: previous }, 'selected')
  }

  if (selectedSlug) {
    map.setFeatureState({ source: WINERY_SOURCE_ID, id: selectedSlug }, { selected: true })
    selectedDots.set(map, selectedSlug)
  } else {
    selectedDots.delete(map)
  }
}
